import {
  ArrowUpRight,
  AtSign,
  BadgeCheck,
  Building2,
  Calendar,
  CircleDollarSign,
  ExternalLink,
  Landmark,
  MapPin,
  Megaphone,
  MessagesSquare,
  NotebookPen,
  Phone,
  Star,
  UserCog,
  UserSearch,
  X,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { ApiError } from '../../api/envelope';
import type { Conversation, DispatchAddress, DispatchCustomerSnapshot } from '../../api/types';
import { ContactAvatar } from '../../components/ContactAvatar';
import { RefreshButton } from '../../components/RefreshButton';
import { money, snippetOf } from '../../lib/commsFormat';
import { channelMetaForInbox } from '../../lib/commsMeta';
import { useTimeFormat } from '../../lib/tenantTime';
import { useLoad } from '../../lib/useLoad';
import { BookingsCard } from './BookingsCard';
import { BookerPicker } from './booking/BookerPicker';
import { bookerLinksFor } from './booking/bookerLinks';
import { hasDispatchBooker } from './gmail/latestBookings';
import { useComms } from './context';
import { profileHref } from './booking/dispatchLink';
import { useDashboardOrigin } from './booking/useDashboardOrigin';
import { useContactDetail } from './useContactDetail';
import { useModuleRefresh } from './useModuleRefresh';
import { DispatchRoleChip } from './DispatchRoleChip';
import { primaryIdentity } from './dispatchIdentity';

/** "1 Bryant Park, Floor 38, New York, NY 10036" — postal code hugs the state. */
function addressLines(address: DispatchAddress): string[] {
  const street = [address.line1, address.line2].filter(Boolean).join(', ');
  const region = [address.city, address.state].filter(Boolean).join(', ');
  const tail = [region, address.postalCode].filter(Boolean).join(' ');
  return [street, tail, address.country ?? ''].filter(Boolean);
}

type LinkTab = 'customers' | 'affiliates' | 'any';

/** What each kind of pick does, for the "?" beside the picker's title. */
const linkTip = (address: string) =>
  `A customer: every message from ${address} shows their reservations, and reservation drafts open on ` +
  `their account. An affiliate: for a reservation desk writing on behalf of passengers — reservation ` +
  `drafts from ${address} are booked for that affiliate.`;

export function ContactPanel({
  conversation,
  onClose,
  onOpenConversation,
}: {
  conversation: Conversation;
  onClose: () => void;
  onOpenConversation: (displayId: number) => void;
}) {
  const { listTime, fmtDateTime } = useTimeFormat();
  const { source, inboxes, agents } = useComms();
  const contactId = conversation.contact.id;
  const bookerLinks = useMemo(() => bookerLinksFor(source), [source]);
  /** Which side the link picker opened on — `any` lets the suggestions decide. */
  const [linkPicker, setLinkPicker] = useState<LinkTab | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const detail = useContactDetail(source, contactId);
  const bookings = useLoad(() => source.api.listContactBookings(contactId), [source, contactId]);
  const history = useLoad(
    () => source.api.listContactConversations(contactId).catch(() => []),
    [source, contactId],
  );

  const refreshPanel = () => {
    detail.reload();
    bookings.reload();
    history.reload();
  };
  useModuleRefresh(refreshPanel);

  const contact = detail.data ?? conversation.contact;
  const customer = detail.data?.dispatchCustomer ?? null;

  /**
   * An agent saying which dispatch customer this address is — the fix for a
   * customer writing from a mailbox dispatch has never heard of, which the
   * exact auto-link can never match. Null is "nobody", which sticks too.
   * Everything on the panel is about the customer, so all of it re-reads.
   */
  const changeLink = async (change: () => Promise<unknown>) => {
    setLinkBusy(true);
    setLinkError(null);
    try {
      await change();
      setLinkPicker(null);
      refreshPanel();
    } catch (e) {
      setLinkError(e instanceof ApiError ? e.message : 'The link could not be changed.');
    } finally {
      setLinkBusy(false);
    }
  };
  const linkedBy =
    contact.dispatchLinkSource === 'manual'
      ? (agents.find((a) => a.dashBoardId === contact.dispatchLinkedBy)?.name ?? 'an agent')
      : null;
  // Dispatch is the system of record for who this person is, so its name wins
  // over the one the messaging channel supplied (a WhatsApp display name, an
  // email From header) whenever the contact is linked.
  const name =
    customer?.name ||
    contact.name ||
    primaryIdentity(contact)?.name ||
    contact.email ||
    contact.phoneNumber ||
    'Unknown';
  // Since WHEN, honestly: dispatch knows when they became a customer, the CRM
  // only knows when they first messaged us.
  const since = customer?.createdAt ?? contact.createdAt;
  const sinceYear = new Date(since).getFullYear();
  // `stale` is another customer's list — see useContactDetail. Same below.
  const previous = (history.stale ? [] : (history.data ?? []))
    .filter((row) => row.id !== conversation.id)
    .slice(0, 3);
  const inboxById = new Map(inboxes.map((inbox) => [inbox.id, inbox]));

  return (
    <div className="ctx">
      <div className="ctx-head">
        <span className="comms-section-label">CONTACT DETAILS</span>
        {/* Nothing on this panel is live: the customer record, the bookings and
            the past threads are all read once when it opens, and the first two
            are dispatch's to change — a ride booked on the phone a minute ago
            is exactly what an agent looks here for. */}
        <RefreshButton
          className="ctx-head-btn"
          size={13}
          label={null}
          title="Refresh contact details"
          refreshing={detail.refreshing || bookings.refreshing || history.refreshing}
          busy={detail.loading || bookings.loading || history.loading}
          onClick={refreshPanel}
        />
        <button type="button" className="ctx-close" onClick={onClose} title="Close details">
          <X size={14} />
        </button>
      </div>

      <div className="ctx-card">
        <div className="ctx-identity">
          <ContactAvatar
            name={name}
            email={customer?.email ?? contact.email}
            photoUrl={detail.data?.avatarUrl}
            size={44}
          />
          <div className="ctx-identity-main">
            <div className="ctx-identity-name" title={name}>
              {name}
            </div>
            {(customer?.jobTitle || customer?.department) && (
              <div className="ctx-identity-role">
                {[customer.jobTitle, customer.department].filter(Boolean).join(' · ')}
              </div>
            )}
            <div className="ctx-identity-meta">
              {/* Priority is the one badge worth shouting: it changes how the
                  agent handles the call, not just how the record reads. */}
              {customer?.priority && Number(customer.priority.code) > 1 && (
                <span
                  className="cv-row-role"
                  style={{ background: 'var(--c-orange-muted)', color: 'var(--c-orange)' }}
                >
                  <Star size={9} style={{ marginRight: 3 }} />
                  {customer.priority.label}
                </span>
              )}
              {customer?.customerType && (
                <span
                  className="cv-row-role"
                  style={{ background: 'var(--c-blue-muted)', color: 'var(--c-blue)' }}
                >
                  {customer.customerType.label}
                </span>
              )}
              <span className="ctx-identity-since">
                Since {Number.isNaN(sinceYear) ? '—' : sinceYear}
              </span>
            </div>
          </div>
        </div>

        <IdentityLines
          contact={contact}
          customer={customer}
          linkedBy={linkedBy}
          linkBusy={linkBusy}
          onLink={(tab) => {
            setLinkError(null);
            setLinkPicker((open) => (open === tab ? null : tab));
          }}
          onUnlink={() => void changeLink(() => bookerLinks.linkCustomer(contactId, null))}
          onForgetAffiliate={() => void changeLink(() => bookerLinks.linkAffiliate(contactId, null))}
        />
        {linkPicker && (
          <BookerPicker
            // Remounted per tab, or a Change pressed on the other line while
            // this is open keeps the tab the first one opened on.
            key={linkPicker}
            contactId={contactId}
            suggest={bookerLinks.suggest}
            initialTab={linkPicker === 'any' ? null : linkPicker}
            title="Link to dispatch"
            busy={linkBusy}
            error={linkError}
            // Each pick sets its own side and leaves the other: who this person
            // is and which desk the address books for are separate answers, and
            // the panel shows both with their own way back.
            onPick={(pick) =>
              void changeLink(() =>
                pick.type === 'customer'
                  ? bookerLinks.linkCustomer(contactId, pick.customer.id)
                  : bookerLinks.linkAffiliate(contactId, pick.affiliate.id),
              )
            }
            onClose={() => setLinkPicker(null)}
            titleTip={linkTip(contact.email ?? contact.phoneNumber ?? 'this contact')}
          />
        )}
        {linkError && !linkPicker && <div className="bkp-note err">{linkError}</div>}
      </div>

      <DispatchCustomerCard
        customer={customer}
        loading={detail.loading}
        listTime={listTime}
        fmtDateTime={fmtDateTime}
      />

      <BookingsCard
        bookings={bookings.stale ? [] : (bookings.data?.payload ?? [])}
        /* A request that FAILED is a third empty, and until now it rendered as
           the second one: no payload, no `unavailable`, so the card said "no
           rides or quotes on this customer yet" about a read that never
           happened. That is the duplicate check's rule on another screen — an
           answer nobody asked for must never read as an answer of none. The
           server's own message is used where there is one (a permission, a
           missing contact), since it says more than any wording here could. */
        unavailable={
          bookings.stale
            ? null
            : bookings.error
              ? bookings.error.message || 'Reservations could not be loaded.'
              : (bookings.data?.unavailable ?? null)
        }
        linked={(bookings.stale ? undefined : bookings.data?.linked) ?? hasDispatchBooker(contact)}
        loading={bookings.loading}
        firstName={name.split(' ')[0]}
      />

      {/* Keyed: the card holds an in-flight toggle of its own, and a panel
          that switched contacts under it showed — and on the next click SAVED —
          the previous customer's owners onto this one. */}
      <ContactOwnersCard key={contactId} contactId={contactId} name={name} />

      {/* A reply to a campaign is usually the lead itself, so the panel says
          which one prompted it — the agent answering knows what offer the
          customer is talking about without asking. */}
      {conversation.campaign && (
        <div className="ctx-card" data-card="campaign-reply">
          <div className="ctx-card-head">
            <Megaphone size={14} />
            <span className="comms-section-label">REPLY TO A CAMPAIGN</span>
          </div>
          <div className="ctx-empty">Started as a reply to “{conversation.campaign.name}”.</div>
        </div>
      )}

      <div className="ctx-card">
        <div className="ctx-card-head">
          <MessagesSquare size={14} />
          <span className="comms-section-label">RECENT CONVERSATIONS</span>
        </div>
        {history.loading && (history.stale || history.data === null) ? (
          // Claiming "the only conversation" before the history lands states
          // something we do not know yet.
          <div className="ctx-empty" aria-busy>
            <span className="sk" style={{ display: 'block', height: 9, width: '70%' }} />
          </div>
        ) : previous.length === 0 ? (
          <div className="ctx-empty">This is the only conversation with {name.split(' ')[0]}.</div>
        ) : (
          previous.map((row) => {
            const inbox = inboxById.get(row.inboxId);
            const meta = inbox ? channelMetaForInbox(inbox) : null;
            return (
              <button
                key={row.id}
                type="button"
                className="ctx-res"
                onClick={() => onOpenConversation(row.id)}
              >
                <div className="ctx-res-top">
                  <span className="ctx-res-when">{listTime(row.lastActivityAt)}</span>
                  <span style={{ flex: 1 }} />
                  <span
                    className="cv-row-role"
                    style={
                      row.status === 'resolved'
                        ? { background: 'var(--c-green-muted)', color: 'var(--c-green)' }
                        : { background: 'var(--c-blue-muted)', color: 'var(--c-blue)' }
                    }
                  >
                    {row.status === 'resolved' ? 'Resolved' : row.status}
                  </span>
                </div>
                <div className="ctx-res-label">
                  {typeof row.additionalAttributes.mailSubject === 'string'
                    ? row.additionalAttributes.mailSubject
                    : snippetOf(row.lastMessage?.content ?? '') || (meta?.label ?? 'Conversation')}
                </div>
                <div className="ctx-res-meta">
                  #{row.id} · {meta?.label ?? inbox?.name ?? ''}
                </div>
              </button>
            );
          })
        )}
      </div>

      <div className="ctx-card">
        <span className="comms-section-label">QUICK ACTIONS</span>
        <div className="ctx-actions">
          <button
            type="button"
            className="ctx-action brand"
            disabled
            title="Available inside the dispatch dashboard"
          >
            <Calendar size={14} />
            Book now from this thread
          </button>
          <button
            type="button"
            className="ctx-action"
            disabled
            title="Available inside the dispatch dashboard"
          >
            <CircleDollarSign size={13} />
            Send a quote
          </button>
          <button
            type="button"
            className="ctx-action"
            disabled
            title="Available inside the dispatch dashboard"
          >
            <ExternalLink size={13} />
            Open customer profile
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * How to reach this person, and how we know it is them.
 *
 * The channel identity (what the contact row was created from) and dispatch's
 * record can legitimately differ — someone emails from a personal address about
 * a corporate account. Both are listed, dispatch first, with the channel one
 * marked so an agent never replies to the wrong address believing it is filed.
 */
function IdentityLines({
  contact,
  customer,
  linkedBy,
  linkBusy,
  onLink,
  onUnlink,
  onForgetAffiliate,
}: {
  contact: Conversation['contact'];
  customer: DispatchCustomerSnapshot | null;
  /** Who linked it by hand; null for an automatic match or no link. */
  linkedBy: string | null;
  linkBusy: boolean;
  onLink: (tab: LinkTab) => void;
  onUnlink: () => void;
  onForgetAffiliate: () => void;
}) {
  // Where this person's own dispatch page is, when there is one to link at —
  // see profileHref. Null origin, passenger, or a record with no id all render
  // exactly as they did before, as text.
  //
  // EMBEDDED ONLY, unlike the booking cards: framed by the dashboard the agent
  // is already signed into it one frame out, so the link lands on the record.
  // Standalone at crm.limosphere.com they may not have a dashboard session at
  // all, and a link that opens a sign-in form is worse than the id they can
  // paste into the dashboard they already have open.
  const { source } = useComms();
  const origin = useDashboardOrigin(source.embedded);
  const dashboard = source.embedded ? origin : null;
  const host = source.openProfile;
  // A click handler only where the parent can act on it AND the record is one
  // it can address — `profileHref` holds the same two rules for the link, and
  // a passenger (no page in the dashboard) fails both.
  const openProfile = useCallback(
    (role: string, id: number | null | undefined) => {
      if (!host || !id) return undefined;
      if (role !== 'customer' && role !== 'affiliate' && role !== 'driver') return undefined;
      return () => host({ kind: role, id });
    },
    [host],
  );
  const affiliateId = contact.dispatchAffiliateId ?? null;
  const affiliateName =
    contact.dispatchAffiliate?.companyName || contact.dispatchAffiliate?.primaryContactName || null;
  const emails: { value: string; note?: string }[] = [];
  const phones: { value: string; note?: string }[] = [];
  const seen = new Set<string>();

  const push = (list: typeof emails, value: string | null, note?: string) => {
    const trimmed = (value ?? '').trim();
    if (!trimmed || seen.has(trimmed.toLowerCase())) return;
    seen.add(trimmed.toLowerCase());
    list.push({ value: trimmed, note });
  };

  // The "from this channel" note only earns its place when dispatch ALSO has an
  // address of that kind — it exists to distinguish two competing values, and on
  // an unlinked contact there is nothing to distinguish it from.
  push(emails, customer?.email ?? null);
  push(emails, contact.email, customer?.email ? 'from this channel' : undefined);
  push(emails, customer?.alternateEmail ?? null, 'alternate');
  push(phones, customer?.phone ?? null);
  push(phones, contact.phoneNumber, customer?.phone ? 'from this channel' : undefined);
  push(phones, customer?.alternatePhone ?? null, 'alternate');

  return (
    <div className="ctx-lines">
      {emails.map((row) => (
        <div className="ctx-line" key={row.value}>
          <AtSign size={13} />
          <a className="ctx-line-link" href={`mailto:${row.value}`} title={row.value}>
            {row.value}
          </a>
          {row.note && <span className="ctx-line-note">{row.note}</span>}
        </div>
      ))}
      {phones.map((row) => (
        <div className="ctx-line" key={row.value}>
          <Phone size={13} />
          <a className="ctx-line-link" href={`tel:${row.value}`} title={row.value}>
            {row.value}
          </a>
          {row.note && <span className="ctx-line-note">{row.note}</span>}
        </div>
      ))}
      {customer?.business && (
        <div className="ctx-line" title="Books under this corporate account">
          <Building2 size={13} />
          <span>{customer.business.name ?? `Account #${customer.business.id}`}</span>
          {customer.isBillingAccount && <span className="ctx-line-note">billing</span>}
        </div>
      )}
      {customer?.address && (
        <div className="ctx-line ctx-line-multi">
          <MapPin size={13} />
          <span>
            {addressLines(customer.address).map((line) => (
              <span key={line} className="ctx-address-line">
                {line}
              </span>
            ))}
          </span>
        </div>
      )}
      {contact.dispatchCustomerId !== null ? (
        <>
          <div className="ctx-line" title="Linked to a dispatch customer record">
            <BadgeCheck size={13} />
            <span className="ctx-link-badge">
              <ProfileLink
                href={profileHref(dashboard, 'customer', contact.dispatchCustomerId)}
                onOpen={openProfile('customer', contact.dispatchCustomerId)}
                label={`Dispatch customer #${contact.dispatchCustomerId}`}
              />
              {contact.dispatchLinkVerified ? ' · verified' : ''}
              {/* Who said so: a wrong link an agent made is a conversation to
                  have, a wrong automatic one is a bug to report. */}
              {linkedBy ? ` · linked by ${linkedBy}` : contact.dispatchLinkSource === 'auto' ? ' · matched' : ''}
            </span>
          </div>
          {/* A line of their own: the panel is narrow, and beside the record
              they squeezed the very id they act on down to an ellipsis. */}
          <div className="ctx-link-actions">
            <button type="button" onClick={() => onLink('customers')} disabled={linkBusy}>
              Change
            </button>
            <button type="button" onClick={onUnlink} disabled={linkBusy}>
              Unlink
            </button>
          </div>
        </>
      ) : (
        // The case this exists for: a customer writing from an address dispatch
        // has no record of. The exact auto-link will never match it, so the
        // agent has to be able to say who it is — once, for every later message.
        // Or a booking desk's: an affiliate's address is no customer at all, so
        // the same Link… offers both, and opens on whichever side matched.
        <>
          <div className="ctx-line">
            <UserSearch size={13} />
            {/* "Not linked to dispatch" is short on purpose: the panel is
                narrow, and "…customer or affiliate" was cut to an ellipsis. */}
            <span className="ctx-line-muted">
              {linkedBy
                ? `Not a dispatch customer · marked by ${linkedBy}`
                : affiliateId
                  ? 'Not linked to a dispatch customer'
                  : 'Not linked to dispatch'}
            </span>
          </div>
          <div className="ctx-link-actions">
            <button
              type="button"
              // With a desk remembered the address is spoken for on that side,
              // which has its own Change; otherwise the suggestions decide.
              onClick={() => onLink(affiliateId ? 'customers' : 'any')}
              disabled={linkBusy}
            >
              Link…
            </button>
          </div>
        </>
      )}
      {affiliateId ? (
        <>
          <div className="ctx-line" title="Reservation drafts from this address open on this affiliate">
            <Building2 size={13} />
            <span className="ctx-link-badge">
              Books for{' '}
              <ProfileLink
                href={profileHref(dashboard, 'affiliate', affiliateId)}
                onOpen={openProfile('affiliate', affiliateId)}
                // The id when dispatch could not name it: the line still says
                // what the booking draft will do.
                label={affiliateName ?? `affiliate #${affiliateId}`}
                dispatchId={affiliateName ? affiliateId : null}
              />
              {/* The customer line's word for it: dispatch has this address on
                  the desk, nobody had to say so. */}
              {contact.dispatchAffiliateLinkSource === 'auto' ? ' · matched' : ''}
            </span>
          </div>
          <div className="ctx-link-actions">
            <button
              type="button"
              onClick={() => onLink('affiliates')}
              disabled={linkBusy}
              aria-label="Change affiliate"
            >
              Change
            </button>
            <button type="button" onClick={onForgetAffiliate} disabled={linkBusy}>
              Forget
            </button>
          </div>
        </>
      ) : null}
      {/*
        Everyone else on this number. One line can be a customer's, a
        passenger's, a chauffeur's and an affiliate's contact number at once,
        and which one is calling changes the whole conversation — so all of them
        are listed rather than only the one that named the thread. Matched on
        the last ten digits, so this is a lead, not the verified link above.
      */}
      {(contact.dispatchIdentity ?? []).map((identity) => (
        <div className="ctx-line" key={`${identity.role}:${identity.id ?? identity.name}`}>
          <UserSearch size={13} />
          <span className="ctx-dispatch-identity">
            <DispatchRoleChip role={identity.role} className="ctx-dispatch-identity-chip" />
            <ProfileLink
              href={profileHref(dashboard, identity.role, identity.id)}
              onOpen={openProfile(identity.role, identity.id)}
              label={identity.name}
              dispatchId={identity.id}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * A name that opens dispatch's own record for that person, three ways, in the
 * order of how well each one lands:
 *
 *   1. Framed by a dashboard that handles OPEN_PROFILE — a button that
 *      navigates the DASHBOARD. No tab, no second sign-in, and the permission
 *      that decides whether this agent may see that record is answered by the
 *      shell that owns it. The conversation stays where it was.
 *   2. Framed by one that does not — the link out that this had before, so an
 *      older dashboard keeps working while the two sides deploy separately.
 *   3. Not framed at all — the id, and no link. Standalone the agent may hold
 *      no dashboard session, and a link that opens a sign-in form is worse
 *      than a number they can paste into the dashboard they already have open.
 *
 * The name is the information in every one of them; the way out is a shortcut
 * on top of it, so all three READ the same.
 */
function ProfileLink({
  href,
  label,
  onOpen,
  dispatchId,
}: {
  href: string | null;
  label: string;
  onOpen?: () => void;
  dispatchId?: number | null;
}) {
  if (onOpen) {
    return (
      <button
        type="button"
        className="ctx-profile-link"
        onClick={onOpen}
        title={`Open ${label} in the dashboard`}
      >
        <span className="ctx-profile-label">{label}</span>
        <ArrowUpRight size={11} />
      </button>
    );
  }
  if (href) {
    return (
      <a
        className="ctx-profile-link"
        href={href}
        target="_blank"
        rel="noreferrer"
        title={`Open ${label} in the dispatch dashboard`}
      >
        {/* The label truncates, not the row: an inline-flex anchor cannot
            ellipsis its own text node, so the text keeps a box of its own. */}
        <span className="ctx-profile-label">{label}</span>
        <ExternalLink size={10} />
      </a>
    );
  }
  return (
    <span className="ctx-profile-link ctx-profile-plain" title={label}>
      <span className="ctx-profile-label">{label}</span>
      {/* The id is what an agent types into the dashboard's own search when
          there is nothing here to click. */}
      {dispatchId ? <span className="ctx-profile-id">#{dispatchId}</span> : null}
    </span>
  );
}

/** Value and history, plus whatever dispatch's staff have written about them. */
function DispatchCustomerCard({
  customer,
  loading,
  listTime,
  fmtDateTime,
}: {
  customer: DispatchCustomerSnapshot | null;
  loading: boolean;
  listTime: (iso: string | null) => string;
  fmtDateTime: (iso: string | null) => string;
}) {
  return (
    <div className="ctx-card">
      <div className="ctx-card-head">
        <Landmark size={14} />
        <span className="comms-section-label">DISPATCH CUSTOMER</span>
        {customer?.isActive === false && (
          <span
            className="cv-row-role"
            style={{ background: 'var(--c-red-muted)', color: 'var(--c-red)' }}
          >
            Inactive
          </span>
        )}
      </div>
      {customer ? (
        <>
          <div className="ctx-kv">
            <span className="ctx-kv-label">Rides</span>
            <span className="ctx-kv-value">{customer.rides ?? '—'}</span>
          </div>
          <div className="ctx-kv">
            <span className="ctx-kv-label">Total spent</span>
            <span className="ctx-kv-value green">{money(customer.totalSpent)}</span>
          </div>
          <div className="ctx-kv">
            <span className="ctx-kv-label">Last ride</span>
            <span className="ctx-kv-value" title={fmtDateTime(customer.lastRide)}>
              {customer.lastRide ? listTime(customer.lastRide) : '—'}
            </span>
          </div>
          {/* Two different notes with two different audiences: `note` is about
              the ride (dispatch prints it for the chauffeur), `internalNote` is
              staff-only. Merging them would put office remarks on a run sheet. */}
          {customer.note && (
            <div className="ctx-note">
              <NotebookPen size={12} />
              <span>{customer.note}</span>
            </div>
          )}
          {customer.internalNote && (
            <div className="ctx-note ctx-note-internal">
              <NotebookPen size={12} />
              <span>
                <b>Internal:</b> {customer.internalNote}
              </span>
            </div>
          )}
        </>
      ) : (
        <div className="ctx-empty">
          {loading
            ? 'Looking up the dispatch customer…'
            : 'No dispatch customer is linked to this contact yet.'}
        </div>
      )}
    </div>
  );
}

/**
 * Who owns this customer. Ownership does two things, and the copy says both
 * because neither is guessable: it makes the owner the first choice for
 * auto-assignment on every channel, and for agents restricted to "Only my
 * customers" it is what decides whether they can see this person at all.
 *
 * Reading and writing owners is `crm_config`, so this card simply does not
 * render for anyone else — a 1036 here is a normal state, not an error.
 */
function ContactOwnersCard({ contactId, name }: { contactId: number; name: string }) {
  const { agents, source } = useComms();
  const owners = useLoad(() => source.api.listContactOwners(contactId), [source, contactId]);
  /**
   * The toggle as pressed, for the length of the save only. It used to stand
   * in for the server's list for good once the first box was ticked, so a
   * colleague's change never reached this card and the module Refresh could
   * not repair it — the fetch landed and was ignored.
   */
  const [draft, setDraft] = useState<number[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useModuleRefresh(owners.reload);

  if (owners.error instanceof ApiError && owners.error.kind === 'forbidden') return null;
  if (owners.loading && !owners.data) return null;

  const current = draft ?? (owners.data ?? []).map((row) => row.dashBoardId);

  const toggle = async (dashBoardId: number) => {
    const next = current.includes(dashBoardId)
      ? current.filter((id) => id !== dashBoardId)
      : [...current, dashBoardId];
    setDraft(next);
    setBusy(true);
    setError(null);
    try {
      const saved = await source.api.setContactOwners(contactId, next);
      // What the server kept becomes the list the boxes are drawn from, and
      // the in-flight copy steps aside — no flicker, since setData is
      // synchronous, and nothing left standing between this card and a reload.
      owners.setData(saved.dashBoardIds.map((id) => ({ dashBoardId: id })));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setDraft(null);
      setBusy(false);
    }
  };

  return (
    <div className="ctx-card">
      <div className="ctx-card-head">
        <UserCog size={14} />
        <span className="comms-section-label">ACCOUNT OWNER</span>
      </div>
      <div className="ctx-owners-hint">
        Owners get {name.split(' ')[0]}&rsquo;s new conversations on any channel, and are always
        notified about them.
      </div>
      {error && <div className="ctx-owners-error">{error}</div>}
      <div className="ctx-owners">
        {agents.length === 0 && <div className="ctx-empty">No agents to choose from.</div>}
        {agents.map((agent) => (
          <label key={agent.dashBoardId} className="ctx-owner">
            <input
              type="checkbox"
              checked={current.includes(agent.dashBoardId)}
              disabled={busy}
              onChange={() => void toggle(agent.dashBoardId)}
            />
            <span className="ctx-owner-name">{agent.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
